import React, { useState, useEffect, useCallback } from 'react';
import { ClipboardList, Users, Bus, User, CheckCircle, Search } from 'lucide-react';
import StatsCard from '../common/StatsCard';
import EmptyState from '../common/EmptyState';
import { AdminWaitlistAPI, APIUtils } from '../../services/api';

// Waitlist signups (landing page) and the follow-up questionnaire answers.
// Read-only: nothing on this page sends, edits or deletes.

const pct = (v) => (v == null ? '—' : `${v}%`);
const fmtDate = (d) => (d ? new Date(d).toLocaleDateString('en-ZA', { day: 'numeric', month: 'short', year: 'numeric' }) : '—');
const fmtDateTime = (d) => (d ? new Date(d).toLocaleString('en-ZA', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }) : '—');
const DEMO_VIDEO_COUNT = 6;
const fmtWeek = (d) => new Date(`${d}T00:00:00`).toLocaleDateString('en-ZA', { day: 'numeric', month: 'short' });

const Card = ({ title, subtitle, children }) => (
  <div className="bg-white rounded-lg shadow-sm border border-gray-200">
    <div className="p-4 border-b border-gray-200">
      <h2 className="text-lg font-semibold text-gray-900">{title}</h2>
      {subtitle && <p className="text-sm text-gray-500 mt-0.5">{subtitle}</p>}
    </div>
    <div className="p-4">{children}</div>
  </div>
);

// Operators who asked for a demo on the questionnaire, and whether they have
// opened the personal demo link we sent them.
const DemoRequests = ({ requests, error }) => (
  <Card title="Demo requests: link opens"
    subtitle={'Only operators who ticked "I want a demo" on the waitlist questionnaire. An open counts once their personal demo link has been on screen for 4 seconds.'}>
    {error ? (
      <p className="text-sm text-red-700">{error}</p>
    ) : !requests ? (
      <p className="text-sm text-gray-500">Loading…</p>
    ) : requests.length === 0 ? (
      <p className="text-sm text-gray-500">No demo requests yet.</p>
    ) : (
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-xs text-gray-500 uppercase">
              <th className="py-2 pr-4">Operator</th>
              <th className="py-2 pr-4">Requested</th>
              <th className="py-2 pr-4 text-right">Opens</th>
              <th className="py-2 pr-4">Last opened</th>
              <th className="py-2 pr-4">Videos played</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {requests.map((r) => (
              <tr key={r.id}>
                <td className="py-2 pr-4">
                  <div className="font-medium text-gray-900">{r.companyName || r.name || '—'}</div>
                  <div className="text-gray-500">{r.email}</div>
                </td>
                <td className="py-2 pr-4 text-gray-500 whitespace-nowrap">{fmtDate(r.requestedAt)}</td>
                <td className="py-2 pr-4 text-right tabular-nums">{r.opens}</td>
                <td className="py-2 pr-4 text-gray-500 whitespace-nowrap"
                  title={r.openTimes.length ? `All opens: ${r.openTimes.map(fmtDateTime).join(', ')}` : undefined}>
                  {r.opens ? fmtDateTime(r.lastOpenedAt) : r.videosPlayed.length ? 'Played a video' : 'Not opened'}
                </td>
                <td className="py-2 pr-4 text-gray-500">{r.videosPlayed.length} of {DEMO_VIDEO_COUNT}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    )}
  </Card>
);


// Weekly signups as stacked CSS columns (operators over travellers).
const WeeklyChart = ({ weekly }) => {
  const max = Math.max(1, ...weekly.map((w) => w.total));
  return (
    <div>
      <div className="flex items-end gap-1.5 h-40" role="img" aria-label="Signups per week, last 12 weeks">
        {weekly.map((w) => (
          <div key={w.weekStart} className="flex-1 flex flex-col items-center justify-end h-full" title={`Week of ${fmtWeek(w.weekStart)}: ${w.operator} operators, ${w.traveller} travellers`}>
            <span className="text-xs text-gray-600 mb-1">{w.total || ''}</span>
            <div className="w-full flex flex-col justify-end" style={{ height: `${(w.total / max) * 100}%` }}>
              <div className="w-full bg-emerald-600 rounded-t" style={{ height: w.total ? `${(w.operator / w.total) * 100}%` : 0 }} />
              <div className={`w-full bg-emerald-300 ${w.operator ? '' : 'rounded-t'}`} style={{ height: w.total ? `${(w.traveller / w.total) * 100}%` : 0 }} />
            </div>
          </div>
        ))}
      </div>
      <div className="flex gap-1.5 mt-1">
        {weekly.map((w) => (
          <span key={w.weekStart} className="flex-1 text-center text-[10px] text-gray-400 truncate">{fmtWeek(w.weekStart)}</span>
        ))}
      </div>
      <div className="flex gap-4 mt-3 text-xs text-gray-600">
        <span className="flex items-center"><span className="w-3 h-3 bg-emerald-600 rounded-sm mr-1.5" />Operators</span>
        <span className="flex items-center"><span className="w-3 h-3 bg-emerald-300 rounded-sm mr-1.5" />Travellers</span>
      </div>
    </div>
  );
};

// One question's answer distribution. Bars are % of respondents; for a
// select-all question they can add up past 100%.
const QuestionBars = ({ q }) => {
  const max = Math.max(1, ...q.options.map((o) => o.count));
  return (
    <div className="mb-5 last:mb-0">
      <div className="flex items-baseline justify-between mb-2">
        <h3 className="text-sm font-medium text-gray-900">{q.label}</h3>
        <span className="text-xs text-gray-500">
          {q.respondents} answered{q.multi ? ' · select all' : ''}
        </span>
      </div>
      {q.respondents === 0 ? (
        <p className="text-xs text-gray-400">No answers yet</p>
      ) : (
        <div className="space-y-1.5">
          {q.options.map((o) => (
            <div key={o.label} className="flex items-center text-sm">
              <span className="w-44 shrink-0 truncate text-gray-600" title={o.label}>{o.label}</span>
              <div className="flex-1 h-3 bg-gray-100 rounded mx-2 overflow-hidden">
                <div className="h-full bg-emerald-500 rounded" style={{ width: `${(o.count / max) * 100}%` }} />
              </div>
              <span className="w-16 text-right text-gray-700 tabular-nums">
                {o.count} <span className="text-gray-400">({Math.round((o.count / q.respondents) * 100)}%)</span>
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const Pill = ({ ok, yes, no }) => (
  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${ok ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-700'}`}>
    {ok ? yes : no}
  </span>
);

const WaitlistPage = () => {
  const [stats, setStats] = useState(null);
  const [statsError, setStatsError] = useState('');
  const [demoRequests, setDemoRequests] = useState(null);
  const [demoError, setDemoError] = useState(null);
  const [signups, setSignups] = useState([]);
  const [pagination, setPagination] = useState({});
  const [loading, setLoading] = useState(true);
  const [role, setRole] = useState('all');
  const [completed, setCompleted] = useState('all');
  const [search, setSearch] = useState('');

  useEffect(() => {
    let live = true;
    AdminWaitlistAPI.getStats()
      .then((res) => { if (live && res.data.success) setStats(res.data.data); })
      .catch((e) => { if (live) setStatsError(APIUtils.handleError(e)); });
    AdminWaitlistAPI.demoRequests()
      .then((res) => { if (live) { if (res.data.success) setDemoRequests(res.data.data.requests); else setDemoError('Could not load demo requests'); } })
      .catch((e) => { if (live) setDemoError(APIUtils.handleError(e)); });
    return () => { live = false; };
  }, []);

  const fetchSignups = useCallback(async (page = 1) => {
    setLoading(true);
    try {
      const params = { page, limit: 25 };
      if (role !== 'all') params.role = role;
      if (completed !== 'all') params.completed = completed;
      if (search) params.search = search;
      const res = await AdminWaitlistAPI.list(params);
      if (res.data.success) {
        setSignups(res.data.data.signups);
        setPagination(res.data.data.pagination);
      }
    } catch (e) {
      console.error('Fetch waitlist error:', APIUtils.handleError(e));
    } finally {
      setLoading(false);
    }
  }, [role, completed, search]);

  useEffect(() => { fetchSignups(); }, [fetchSignups]);

  const c = stats?.completion;

  return (
    <div>
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Waitlist</h1>
        <p className="text-gray-500 mt-1">Landing-page signups and questionnaire answers</p>
      </div>

      {statsError && (
        <div className="mb-6 p-3 bg-red-50 border border-red-200 text-red-700 text-sm rounded-lg">{statsError}</div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <StatsCard title="Total Signups" value={stats?.totals.all ?? '—'} icon={Users} color="emerald"
          subtitle={stats ? `${stats.weekly[stats.weekly.length - 1]?.total || 0} this week` : undefined} />
        <StatsCard title="Operators" value={stats?.totals.operator ?? '—'} icon={Bus} color="blue"
          subtitle={stats ? `${stats.demoRequests} want a demo` : undefined} />
        <StatsCard title="Travellers" value={stats?.totals.traveller ?? '—'} icon={User} color="purple"
          subtitle={stats?.totals.excludedTest ? `${stats.totals.excludedTest} test sign-ups hidden` : undefined} />
        <StatsCard title="Questionnaire Completion" value={c ? pct(c.overall.completionRate) : '—'} icon={CheckCircle} color="green"
          subtitle={c ? `Operators ${pct(c.operator.completionRate)} · Travellers ${pct(c.traveller.completionRate)}` : undefined} />
      </div>

      <div className="mb-8">
        <DemoRequests requests={demoRequests} error={demoError} />
      </div>

      {stats && (
        <>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
            <Card title="Signups per week" subtitle="Last 12 weeks, Monday start (SAST)">
              <WeeklyChart weekly={stats.weekly} />
            </Card>
            <Card title="Funnel" subtitle="Signed up → welcome sent → questionnaire done">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-xs text-gray-500 uppercase">
                    <th className="py-2"></th><th className="py-2 text-right">Signed up</th>
                    <th className="py-2 text-right">Welcomed</th><th className="py-2 text-right">Completed</th>
                    <th className="py-2 text-right">Rate</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {[['Operators', c.operator], ['Travellers', c.traveller], ['All', c.overall]].map(([label, f]) => (
                    <tr key={label} className={label === 'All' ? 'font-semibold' : ''}>
                      <td className="py-2 text-gray-900">{label}</td>
                      <td className="py-2 text-right tabular-nums">{f.signups}</td>
                      <td className="py-2 text-right tabular-nums">{f.welcomed}</td>
                      <td className="py-2 text-right tabular-nums">{f.submitted}</td>
                      <td className="py-2 text-right tabular-nums">{pct(f.completionRate)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </Card>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
            <Card title="Operator questionnaire" subtitle={`${c.operator.submitted} completed · counts per option, % of those who answered`}>
              {stats.questions.operator.map((q) => <QuestionBars key={q.key} q={q} />)}
            </Card>
            <Card title="Traveller questionnaire" subtitle={`${c.traveller.submitted} completed · counts per option, % of those who answered`}>
              {stats.questions.traveller.map((q) => <QuestionBars key={q.key} q={q} />)}
            </Card>
          </div>
        </>
      )}

      {/* Filters */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 mb-6">
        <div className="p-4 flex flex-wrap items-center justify-between gap-4">
          <div className="flex flex-wrap gap-4">
            <div className="flex space-x-1">
              {['all', 'operator', 'traveller', 'driver'].map((r) => (
                <button key={r} onClick={() => setRole(r)}
                  className={`px-3 py-1.5 text-sm font-medium rounded-lg capitalize transition-colors ${role === r ? 'bg-emerald-100 text-emerald-700' : 'text-gray-600 hover:bg-gray-100'}`}>
                  {r === 'all' ? 'All roles' : `${r}s`}
                </button>
              ))}
            </div>
            <div className="flex space-x-1">
              {[['all', 'Any'], ['yes', 'Completed'], ['no', 'Not completed']].map(([v, label]) => (
                <button key={v} onClick={() => setCompleted(v)}
                  className={`px-3 py-1.5 text-sm font-medium rounded-lg transition-colors ${completed === v ? 'bg-emerald-100 text-emerald-700' : 'text-gray-600 hover:bg-gray-100'}`}>
                  {label}
                </button>
              ))}
            </div>
          </div>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={16} />
            <input type="text" placeholder="Search name, email, company..." value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-9 pr-4 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 outline-none" />
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-600"></div>
          </div>
        ) : signups.length === 0 ? (
          <EmptyState title="No signups found" message="No waitlist signups match the current filters." icon={ClipboardList} />
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    {['Name', 'Email', 'Role', 'Company', 'Signed up', 'Welcome', 'Questionnaire'].map((h) => (
                      <th key={h} className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {signups.map((s) => (
                    <tr key={s.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 text-sm font-medium text-gray-900">{s.name || '—'}</td>
                      <td className="px-6 py-4 text-sm text-gray-500">{s.email}</td>
                      <td className="px-6 py-4 text-sm text-gray-700 capitalize">{s.role}</td>
                      <td className="px-6 py-4 text-sm text-gray-500">
                        {s.companyName || '—'}
                        {s.wantsDemo && <span className="ml-2 px-2 py-0.5 bg-blue-100 text-blue-800 text-xs rounded-full">demo</span>}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-500 whitespace-nowrap">{fmtDate(s.createdAt)}</td>
                      <td className="px-6 py-4"><Pill ok={s.welcomeSent} yes="Sent" no="Not sent" /></td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <Pill ok={s.completed} yes={`Done ${fmtDate(s.formSubmittedAt)}`} no="Pending" />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {pagination.pages > 1 && (
              <div className="px-6 py-3 border-t border-gray-200 flex items-center justify-between">
                <p className="text-sm text-gray-500">
                  Showing {((pagination.page - 1) * pagination.limit) + 1} to {Math.min(pagination.page * pagination.limit, pagination.total)} of {pagination.total}
                </p>
                <div className="flex space-x-2">
                  <button onClick={() => fetchSignups(pagination.page - 1)} disabled={pagination.page <= 1}
                    className="px-3 py-1 text-sm border border-gray-300 rounded-lg disabled:opacity-50 hover:bg-gray-50">Previous</button>
                  <button onClick={() => fetchSignups(pagination.page + 1)} disabled={pagination.page >= pagination.pages}
                    className="px-3 py-1 text-sm border border-gray-300 rounded-lg disabled:opacity-50 hover:bg-gray-50">Next</button>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default WaitlistPage;
